import { Component, computed, HostBinding, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppFooter } from './app.footer';
import { LayoutService, TabCloseEvent } from '@/layout/service/layout.service';
import { AppConfigurator } from './app.configurator';
import {AppSidebar} from "./app.sidebar";
import { BackgroundTileService } from '@/services/background-tile.service';
import { AuthService } from '@/services/auth.service';
import {
    RecurringInvoiceCronAlertService,
    RecurringInvoiceCronStatus,
} from '@/services/recurring-invoice-cron-alert.service';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, AppFooter, AppConfigurator],
    template: `
        @if (isAdmin && cronStatus && cronStatus.stale) {
            <div style="background: #dc2626; color: white; text-align: center; padding: 6px 12px; font-weight: 600; font-size: 13px; position: fixed; top: 0; left: 0; right: 0; z-index: 9999; cursor: pointer; height: 32px; box-sizing: border-box;"
                 (click)="goToSystemStatus()">
                {{ cronBannerText() }}
                <span style="text-decoration: underline; margin-left: 8px;">View details &rarr;</span>
            </div>
        }
        <div class="layout-container" [ngClass]="containerClass">
            <div app-topbar></div>
            <div app-sidebar></div>
            <div class="layout-content-wrapper">
                <div class="layout-content" [ngStyle]="backgroundStyle()">
                    <div class="layout-content-inner">
                        <router-outlet></router-outlet>
                        <div app-footer></div>
                    </div>
                </div>
            </div>
        </div>
        <app-configurator />
    `,
})
export class AppLayout implements OnDestroy {
    cronStatus: RecurringInvoiceCronStatus | null = null;
    private cronStatusSubscription?: Subscription;

    isAdmin = false;
    private currentUserSubscription?: Subscription;

    /**
     * Sets --banner-offset on the host so the fixed topbar shifts down when a
     * banner is showing; otherwise the 32px banner covers the topbar.
     */
    @HostBinding('style.--banner-offset')
    get bannerOffset(): string {
        return `${this.isAdmin && this.cronStatus?.stale ? 32 : 0}px`;
    }

    overlayMenuOpenSubscription: Subscription;

    tabOpenSubscription: Subscription;

    tabCloseSubscription: Subscription;

    menuOutsideClickListener: any;

    menuScrollListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopbar!: AppTopbar;

    backgroundStyle = computed(() => ({
        'background-color': 'var(--surface-ground)',
        'background-image': `url(${this.backgroundTileService.backgroundUrl()})`,
        'background-repeat': 'repeat',
        'background-size': '400px 400px',
        'background-position': 'center'
    }));

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private backgroundTileService: BackgroundTileService,
        private recurringInvoiceCronAlertService: RecurringInvoiceCronAlertService,
        private authService: AuthService
    ) {
        this.currentUserSubscription = this.authService.currentUser$.subscribe((user) => {
            this.isAdmin = user?.role === 'admin';
        });
        this.cronStatusSubscription = this.recurringInvoiceCronAlertService.status$.subscribe((status) => {
            this.cronStatus = status;
        });
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    const isOutsideClicked = !(
                        this.appSidebar.appMenu.el.nativeElement.isSameNode(event.target) ||
                        this.appSidebar.appMenu.el.nativeElement.contains(event.target) ||
                        this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
                        this.appTopbar.menuButton.nativeElement.contains(event.target)
                    );
                    if (isOutsideClicked) {
                        this.hideMenu();
                    }
                });
            }

            if ((this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && !this.menuScrollListener) {
                this.menuScrollListener = this.renderer.listen(this.appSidebar.appMenu.menuContainer.nativeElement, 'scroll', (event) => {
                    if (this.layoutService.isDesktop()) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });

        this.tabOpenSubscription = this.layoutService.tabOpen$.subscribe((tab) => {
            this.router.navigate(tab.routerLink);
            this.layoutService.openTab(tab);
        });

        this.tabCloseSubscription = this.layoutService.tabClose$.subscribe((event: TabCloseEvent) => {
            if (this.router.isActive(event.tab.routerLink[0], { paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' })) {
                const tabs = this.layoutService.tabs;

                if (tabs.length > 1) {
                    if (event.index === tabs.length - 1) this.router.navigate(tabs[tabs.length - 2].routerLink);
                    else this.router.navigate(tabs[event.index + 1].routerLink);
                } else {
                    this.router.navigate(['/']);
                }
            }

            this.layoutService.closeTab(event.index);
        });
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        this.layoutService.reset();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }

        if (this.menuScrollListener) {
            this.menuScrollListener();
            this.menuScrollListener = null;
        }

        this.unblockBodyScroll();
    }

    cronBannerText(): string {
        const status = this.cronStatus;
        if (!status || !status.stale) return '';
        if (status.reason === 'never-run') {
            return 'Recurring invoice cron has never run.';
        }
        const days = Math.floor((status.hoursSince ?? 0) / 24);
        return `Recurring invoice cron hasn't run since ${days} day${days === 1 ? '' : 's'}.`;
    }

    goToSystemStatus() {
        this.router.navigate(['/admin/system-status']);
    }

    get containerClass() {
        return {
            'layout-slim': this.layoutService.layoutConfig().menuMode === 'slim',
            'layout-slim-plus': this.layoutService.layoutConfig().menuMode === 'slim-plus',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive,
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-light': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && !this.layoutService.isDarkTheme(),
            'layout-dark': this.layoutService.layoutConfig().layoutTheme === 'colorScheme' && this.layoutService.isDarkTheme(),
            'layout-primary': !this.layoutService.isDarkTheme() && this.layoutService.layoutConfig().layoutTheme === 'primaryColor'
        };
    }

    ngOnDestroy() {
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }

        if (this.tabOpenSubscription) {
            this.tabOpenSubscription.unsubscribe();
        }

        if (this.tabCloseSubscription) {
            this.tabCloseSubscription.unsubscribe();
        }

        if (this.cronStatusSubscription) {
            this.cronStatusSubscription.unsubscribe();
        }

        if (this.currentUserSubscription) {
            this.currentUserSubscription.unsubscribe();
        }
    }
}
